import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, Calendar, Phone, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Prefissi telefonici europei supportati
const europeanPrefixes = [
  { code: '+39', country: 'Italia', flag: '🇮🇹' },
  { code: '+33', country: 'Francia', flag: '🇫🇷' },
  { code: '+34', country: 'Spagna', flag: '🇪🇸' },
  { code: '+49', country: 'Germania', flag: '🇩🇪' },
  { code: '+44', country: 'Regno Unito', flag: '🇬🇧' },
  { code: '+31', country: 'Paesi Bassi', flag: '🇳🇱' },
  { code: '+32', country: 'Belgio', flag: '🇧🇪' },
  { code: '+41', country: 'Svizzera', flag: '🇨🇭' },
  { code: '+43', country: 'Austria', flag: '🇦🇹' },
  { code: '+351', country: 'Portogallo', flag: '🇵🇹' },
  { code: '+48', country: 'Polonia', flag: '🇵🇱' },
  { code: '+30', country: 'Grecia', flag: '🇬🇷' },
  { code: '+353', country: 'Irlanda', flag: '🇮🇪' },
];

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [showDobForm, setShowDobForm] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [phone, setPhone] = useState('');
  const [phonePrefix, setPhonePrefix] = useState('+39');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');

  const getFullPhoneNumber = () => {
    const cleanPhone = phone.replace(/\s/g, '').replace(/^0+/, '');
    return `${phonePrefix}${cleanPhone}`;
  };

  const sendOTP = async () => {
    if (!phone || phone.replace(/\s/g, '').length < 6) {
      toast.error('Inserisci un numero di telefono valido');
      return;
    }

    const fullPhone = getFullPhoneNumber();
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-otp`, { phone: fullPhone });
      toast.success('Codice OTP inviato al tuo telefono');
      setOtpSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Inserisci il codice a 6 cifre');
      return;
    }

    const fullPhone = getFullPhoneNumber();

    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-otp`, { 
        phone: fullPhone, 
        code: otpCode 
      });
      toast.success('Numero verificato!');
      setPhoneVerified(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codice errato');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processGoogleAuth = async () => {
      // Extract session_id from URL fragment
      const hash = location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        toast.error('Sessione Google non valida');
        navigate('/auth');
        return;
      }

      try {
        // Send session_id to backend
        const response = await fetch(`${API}/auth/google/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Autenticazione fallita');
        }

        const data = await response.json();

        // Store token and user data
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));

        // Check if user needs to complete profile (date_of_birth)
        if (data.needs_date_of_birth) {
          setUser(data.user);
          setShowDobForm(true);
          toast.info('Completa il tuo profilo per continuare');
        } else {
          toast.success(`Benvenuto ${data.user.first_name}!`);
          // Redirect based on role
          const role = data.user.role;
          if (role === 'admin') {
            navigate('/dashboard/admin');
          } else if (role === 'doctor') {
            navigate('/dashboard/doctor');
          } else {
            navigate('/dashboard/patient');
          }
        }
      } catch (error) {
        console.error('Google auth error:', error);
        toast.error('Errore durante l\'autenticazione con Google');
        navigate('/auth');
      }
    };

    processGoogleAuth();
  }, [navigate, location]);

  const handleCompleteProfile = async (e) => {
    e.preventDefault();
    
    if (!dateOfBirth) {
      toast.error('La data di nascita è obbligatoria');
      return;
    }

    if (!phone || !phoneVerified) {
      toast.error('Verifica il numero di telefono per continuare');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/auth/google/complete-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          date_of_birth: dateOfBirth,
          phone: getFullPhoneNumber()
        })
      });

      if (!response.ok) {
        throw new Error('Errore aggiornamento profilo');
      }

      const data = await response.json();
      localStorage.setItem('user', JSON.stringify(data.user));
      
      toast.success('Profilo completato!');
      navigate('/dashboard/patient');
    } catch (error) {
      toast.error('Errore durante l\'aggiornamento del profilo');
    } finally {
      setLoading(false);
    }
  };

  if (showDobForm) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{background: 'linear-gradient(135deg, rgba(127, 179, 160, 0.15) 0%, rgba(242, 239, 233, 0.6) 100%)'}}>
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold" style={{color: '#2A9FCB'}}>
              Completa il tuo profilo
            </h2>
            <p className="text-[#2A9FCB]/80 mt-2">
              Ciao {user?.first_name}! Abbiamo bisogno di alcune informazioni.
            </p>
          </div>

          <div className="rounded-2xl shadow-lg p-8" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
            <form onSubmit={handleCompleteProfile} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>
                  Data di Nascita *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                  <input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                    style={{borderColor: '#E5E7EB'}}
                    required
                    max={new Date().toISOString().split('T')[0]}
                    data-testid="dob-input"
                  />
                </div>
                <p className="text-xs text-[#2A9FCB]/70 mt-1">Necessaria per personalizzare i servizi</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>
                  Telefono * <span className="text-xs font-normal">(richiede verifica)</span>
                </label>
                <div className="flex gap-2 mb-2">
                  <select
                    value={phonePrefix}
                    onChange={(e) => setPhonePrefix(e.target.value)}
                    className="w-28 py-3 px-2 border rounded-lg focus:outline-none focus:ring-2 text-sm"
                    style={{borderColor: '#E5E7EB'}}
                    data-testid="phone-prefix-select"
                    disabled={phoneVerified}
                  >
                    {europeanPrefixes.map(p => (
                      <option key={p.code} value={p.code}>
                        {p.flag} {p.code}
                      </option>
                    ))}
                  </select>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                    <input
                      type="tel"
                      placeholder="333 123 4567"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/[^\d\s]/g, ''))}
                      className="w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                      style={{borderColor: phoneVerified ? '#10B981' : '#E5E7EB'}}
                      data-testid="phone-input"
                      disabled={phoneVerified}
                    />
                  </div>
                </div>
                
                {phoneVerified ? (
                  <div className="flex items-center gap-2 text-green-600 text-sm p-2 bg-green-50 rounded-lg">
                    <CheckCircle className="w-4 h-4" />
                    <span>Numero {getFullPhoneNumber()} verificato!</span>
                  </div>
                ) : !otpSent ? (
                  <button
                    type="button"
                    onClick={sendOTP}
                    disabled={loading || !phone}
                    className="w-full py-2 rounded-lg font-medium text-white flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                    style={{background: '#2A9FCB'}}
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Invia Codice SMS'}
                  </button>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      className="w-full px-4 py-2 text-center text-xl tracking-widest border rounded-lg"
                      style={{borderColor: '#E5E7EB'}}
                      placeholder="000000"
                    />
                    <button
                      type="button"
                      onClick={verifyOTP}
                      disabled={loading || otpCode.length !== 6}
                      className="w-full py-2 rounded-lg font-medium text-white flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                      style={{background: '#10B981'}}
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verifica Codice'}
                    </button>
                  </div>
                )}
                <p className="text-xs text-[#2A9FCB]/70 mt-1">Solo prefissi europei accettati</p>
              </div>

              <button
                type="submit"
                disabled={loading || !phoneVerified}
                className="w-full py-3 rounded-lg font-medium text-white flex items-center justify-center gap-2 disabled:opacity-50"
                style={{background: '#2A9FCB'}}
                data-testid="complete-profile-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Completa Registrazione'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, rgba(127, 179, 160, 0.15) 0%, rgba(242, 239, 233, 0.6) 100%)'}}>
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{color: '#2A9FCB'}} />
        <p className="text-[#2A9FCB]/80">Autenticazione in corso...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
